import { motion } from "framer-motion";
import { AppWindow, Boxes, Gamepad2, KeyRound, Globe2, CloudCog } from "lucide-react";

const FEATURES = [
  {
    icon: AppWindow,
    title: "RaavaForge Studio",
    tag: "DESKTOP WORKSPACE",
    accent: "#4DA3FF",
    accentRgb: "77, 163, 255",
    desc: "One workspace for the media and utility tasks I use every day.",
    points: ["Online media & playlists", "Convert, extract & trim", "Images, QR codes & organized output"],
  },
  {
    icon: Boxes,
    title: "Raava Multi Launcher",
    tag: "UNIFIED TOOL HUB",
    accent: "#A78BFA",
    accentRgb: "167, 139, 250",
    desc: "A single home for my growing collection of development tools.",
    points: ["RTM tools & PS3 debugger", "Memory, scanner & byte utilities", "Shared navigation, design & updates"],
  },
  {
    icon: Gamepad2,
    title: "PS3 Tooling",
    tag: "CONSOLE DEVELOPMENT",
    accent: "#00E5FF",
    accentRgb: "0, 229, 255",
    desc: "The low-level toolkit behind my PS3 menus and RTM work.",
    points: ["TMAPI & CCAPI workflows", "Read, write, scan & debug memory", "Built and tested on real hardware"],
  },
  {
    icon: KeyRound,
    title: "License System",
    tag: "ACCESS & CONTROL",
    accent: "#F5B942",
    accentRgb: "245, 185, 66",
    desc: "One flexible access model shared across Raava projects.",
    points: ["Project-specific permissions", "Durations & device binding", "Live changes across web & desktop"],
  },
  {
    icon: Globe2,
    title: "Raava Portal",
    tag: "WEB EXPERIENCE",
    accent: "#32D583",
    accentRgb: "50, 213, 131",
    desc: "The public home and account area for everything Raava.",
    points: ["Email or license-key sign-in", "Downloads, releases & project pages", "Website and desktop connected"],
  },
  {
    icon: CloudCog,
    title: "Connected Backend",
    tag: "AUTH & LIVE DATA",
    accent: "#5FA8FF",
    accentRgb: "95, 168, 255",
    desc: "The secure layer keeping every connected project in sync.",
    points: ["Authentication & protected license data", "Live permissions & update delivery", "A foundation for future projects"],
  },
];

export default function Features() {
  return (
    <section id="features" className="relative px-6 sm:px-12 lg:px-24 py-32 border-t border-[#222222]" data-testid="features-section">
      <div className="relative mx-auto mb-20 max-w-5xl text-center">
        <div>
          <p className="mb-6 text-center text-xs uppercase tracking-[0.4em] text-[#35AFC0]">// System Modules</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F4F4F5]" style={{ fontWeight: 700 }}>
            The parts that make Raava work.
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {FEATURES.map((f, i) => (
          <motion.div
            key={f.title}
            initial={{ y: 24 }}
            whileInView={{ y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
            className="feature-readable-card raava-glow-panel group min-h-[290px] border border-white/10 rounded-2xl p-7 sm:p-8 hover:-translate-y-1 transition-[transform,background-color,border-color] duration-300 relative overflow-hidden shadow-[0_22px_70px_rgba(0,0,0,.24)]"
            style={{ "--panel-accent-rgb": f.accentRgb }}
            data-testid={`feature-card-${i}`}
          >
            <div
              className="absolute top-0 left-8 right-8 h-px opacity-80"
              style={{ backgroundImage: `linear-gradient(90deg, transparent, ${f.accent}, transparent)` }}
            />
            <div
              className="w-11 h-11 rounded-xl border flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-105"
              style={{ borderColor: `${f.accent}55`, backgroundColor: `${f.accent}10`, color: f.accent }}
            >
              <f.icon className="w-5 h-5" strokeWidth={1.7} />
            </div>
            <p className="text-[10px] uppercase tracking-[0.24em] mb-3" style={{ color: f.accent }}>{f.tag}</p>
            <h3 className="font-display text-xl tracking-tight text-[#F4F4F5] mb-3" style={{ fontWeight: 600 }}>{f.title}</h3>
            <p className="text-sm text-[#A9A9B3] leading-6 mb-5">{f.desc}</p>
            <ul className="space-y-2">
              {f.points.map((point) => (
                <li key={point} className="flex items-start gap-3 text-xs sm:text-sm text-[#D1D1D7] leading-5">
                  <span className="mt-[0.48rem] h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: f.accent, boxShadow: `0 0 10px ${f.accent}88` }} />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
